/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/
import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Typography from "@mui/material/Typography";
import { InputAdornment, TextField } from "@mui/material";
import Box from "@mui/material/Box";
import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import AcceptNewUserModal from "./AcceptNewUserModal";
import RejectUserModal from "./RejectUserModal";
import DeactivateModal from "./DeactivateModal";
import DeleteUserModal from "./DeleteUserModal";
import APIManager from "../../../../Services/ApiManager/ApiManager";
import {
  reactivateUser,
} from "../../../../Services/redux/global-admin-users/GlobalAdminUsersActions";
import { useDispatch } from "react-redux";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

const CustomBoxRow = styled(Box)({
  maxWidth: "727px",
  minHeight: "63px",
  display: "flex",
  flexDirection: "row",
  gap: "36px",
});

const CustomBoxRowItem = styled(Box)({
  width: "355.5px",
  minHeight: "63px",
  display: "flex",
  flexDirection: "column",
  gap: "4px",
});

const CustomLabelTypo = styled(Typography)({
  width: "74px",
  height: "19px",
  fontWeight: 600,
  fontSize: "16px",
  lineHeight: "100%",
  letterSpacing: "0px",
  verticalAlign: "middle",
  color: "#2A2C2D",
  textWrap: "nowrap",
});

const CustomTextField = styled(TextField)(({ disabled }) => ({
  maxWidth: "355.5px",
  maxHeight: "40px",
  border: "1px solid #1214151A",
  borderRadius: "4px",
  color: "#717273",
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "& .MuiInputBase-input": {
    color: disabled ? "#717273" : "inherit",
    padding: "4px 8px",
  },
  "& .MuiOutlinedInput-root": {
    backgroundColor: disabled ? "#F3F3F3" : "#ffffff",
  },
}));

const CustomSingleInput = styled(TextField)(({ disabled }) => ({
  maxWidth: "727px",
  maxHeight: "40px",
  border: "1px solid #1214151A",
  borderRadius: "4px",
  color: "#717273",
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "& .MuiInputBase-input": {
    color: disabled ? "#717273" : "inherit",
    padding: "4px 8px",
  },
  "& .MuiOutlinedInput-root": {
    backgroundColor: disabled ? "#F3F3F3" : "#ffffff",
  },
}));

function UserDetailsModal({
  openUserDetailsModal,
  handleUserDetailsClose,
  userDetails,
  searchTerm,
  currentTabStatus,
}) {
  const dispatch = useDispatch();
  const [disabled, setDisabled] = useState(true);
  const [enrichedUserDetails, setEnrichedUserDetails] = useState(null);

  const [openAcceptNewUserModal, setOpenAcceptNewUserModal] = useState(false);
  const [openRejectUserModal, setOpenRejectUserModal] = useState(false);
  const [openDeactivateModal, setOpenDeactivateModal] = useState(false);
  const [openDeleteUserModal, setOpenDeleteUserModal] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState(userDetails?.id);

  const [userName, setUserName] = useState(
    `${userDetails?.givenName} ${userDetails?.familyName}`
  );
  const [userEmail, setUserEmail] = useState(userDetails?.email || "");
  const [companyNameState, setCompanyNameState] = useState(
    userDetails?.companyName || ""
  );
  const [companyWebsiteURL, setCompanyWebsiteURL] = useState(
    userDetails?.companyWebsite || ""
  );

  const readOnly_GlobalAdmin =
    sessionStorage.getItem("readonly_GlobalAdmin") === "true";

  const handleAcceptModalOpen = (index) => {
    setSelectedRowId(index);
    setOpenAcceptNewUserModal(true);
  };

  const handleAcceptModalClose = () => {
    setOpenAcceptNewUserModal(false);
    handleUserDetailsClose();
  };

  const handleRejectModalOpen = (userId) => {
    setSelectedRowId(userId);
    setOpenRejectUserModal(true);
  };

  const handleRejectModalClose = () => {
    setOpenRejectUserModal(false);
    handleUserDetailsClose();
  };

  const handleDeactivateClose = () => {
    setOpenDeactivateModal(false);
    handleUserDetailsClose(); // Close the parent modal after deactivation
  };

  const handleDeleteModalOpen = (userId) => {
    setSelectedRowId(userId);
    setOpenDeleteUserModal(true);
  };

  const handleDeleteUserClose = () => {
    setOpenDeleteUserModal(false);
    handleUserDetailsClose(); // Close the parent modal after deletion
  };

  useEffect(() => {
    if (userDetails && openUserDetailsModal) {
      // Reset disabled state when userDetails changes
      setDisabled(true);
      // Reset form state when userDetails changes (only when modal opens)
      setUserName(userDetails?.firstName || "");
      setUserEmail(userDetails?.email || "");
      setCompanyNameState(userDetails?.companyName || "");
      setCompanyWebsiteURL(userDetails?.companyWebsite || "");

      // Fetch detailed user info to get agreementDetails
      const fetchUserDetails = async () => {
        try {
          const response = await APIManager.getGlobalAdminUserById(userDetails.id);
          // Only merge specific fields from the detailed response
          const detailedData = response.results?.[0] || response;
          setEnrichedUserDetails({
            ...userDetails,
            agreementDetails: detailedData.agreementDetails,
          });
        } catch (error) {
          console.error("Failed to fetch user details:", error);
          setEnrichedUserDetails(userDetails);
        }
      };

      if (userDetails.id) {
        fetchUserDetails();
      } else {
        setEnrichedUserDetails(userDetails);
      }
    }
  }, [userDetails, openUserDetailsModal]);

  useEffect(() => {
    // Reset disabled state when modal closes
    if (!openUserDetailsModal) {
      setDisabled(true);
    }
  }, [openUserDetailsModal]);

  const handleActivateDeactivateUser = () => {
    if (userDetails?.status === "DEACTIVATED") {
      // Logic from handleReactivateUser in DeactivatedTableData.jsx
      dispatch(reactivateUser(userDetails.id, searchTerm?.trim()));
      handleUserDetailsClose(); // Close the modal after action
    } else if (userDetails?.status === "ACTIVE") {
      // Logic from handleDeactivateOpen in ActiveTableData.jsx
      // Open the deactivate modal to collect reason
      setSelectedRowId(userDetails.id);
      setOpenDeactivateModal(true);
    }
  };

  const handleViewButtonClick = () => {
  };

  const handleEdit = () => {
    setDisabled(false); // Toggle the disabled state
  };

  const handleCancelUserEdit = () => {
    // Reset form state to original values
    setUserName(userDetails?.firstName || "");
    setUserEmail(userDetails?.email || "");
    setCompanyNameState(userDetails?.companyName || "");
    setCompanyWebsiteURL(userDetails?.companyWebsite || "");
    setDisabled(true); // Revert back to disabled state
  };

  const handleSaveUserEdit = () => {
    setDisabled(true);
  };

  const handleViewAgreementClick = () => {
    const agreementUrl = displayUserDetails?.agreementDetails?.url;
    if (agreementUrl) {
      window.open(agreementUrl, "_blank", "noopener,noreferrer");
    }
  };

  const displayUserDetails = enrichedUserDetails || userDetails;

  const isViewMode =
    ((userDetails?.status === "ACTIVE" && disabled) ||
      (userDetails?.status === "DEACTIVATED" && disabled)) &&
    userDetails?.status !== "PENDING";

  const isEditMode =
    (userDetails?.status === "ACTIVE" && !disabled) ||
    (userDetails?.status === "DEACTIVATED" && !disabled);

  const agreementAcceptedValue = (() => {
    if (displayUserDetails?.status === "PENDING") return "Not Available";
    if (displayUserDetails?.agreementDetails?.version)
      return `Yes - Version ${displayUserDetails.agreementDetails.version}`;
    return "Not Available";
  })();

  const agreementTypeLabel = (() => {
    if (displayUserDetails?.status === "PENDING") return "";
    if (displayUserDetails?.agreementDetails?.isDefault === true) return "Custom Agreement";
    if (displayUserDetails?.agreementDetails?.isDefault === false) return "Pre Approved Agreement";
    return "";
  })();

  return (
    <>
      <BootstrapDialog
        onClose={handleUserDetailsClose}
        aria-labelledby="customized-dialog-title"
        open={openUserDetailsModal}
        sx={{
          "& .MuiDialog-root": {
            zIndex: 1300,
          },
          "& .MuiDialog-container": {
            alignItems: "flex-start",
            justifyContent: "center",
            paddingTop: "40px",
            paddingBottom: "40px",
            paddingLeft: "8px",
            paddingRight: "8px",
            minHeight: "100vh",
            boxSizing: "border-box",
          },
          "& .MuiDialog-paper": {
            position: "relative",
            margin: "0",
            marginTop: "0",
            marginBottom: "0",
          },
        }}
        slotProps={{
          paper: {
            sx: {
              maxWidth: "775px",
              width: "100%",
              height: "auto",
              maxHeight: "calc(100vh - 120px)",
              border: "1px solid #1214151A",
              borderRadius: "8px",
              padding: "24px 24px 40px 24px",
              display: "flex",
              flexDirection: "column",
              gap: "24px",
              overflow: "hidden",
            },
          },
        }}
      >
        <Box
          sx={{
            maxWidth: "727px",
            minHeight: "36px",
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          <Typography
            component="h2"
            sx={{
              color: "#121415",
              fontWeight: 500,
              fontSize: "22px",
              lineHeight: "120%",
              letterSpacing: "0%",
            }}
          >
            User Details
          </Typography>

          <Box
            sx={{
              maxWidth: "344px",
              maxHeight: "36px",
              display: "flex",
              gap: "16px",
              alignItems: "center",
              flexDirection: "row",
            }}
          >
            {isViewMode && (
              <DialogActions disabled={readOnly_GlobalAdmin}>
                <Button
                  sx={{
                    textTransform: "none",
                  }}
                  variant="contained"
                  onClick={handleEdit}
                  disabled={readOnly_GlobalAdmin}
                >
                  Edit
                </Button>
                <Button
                  sx={{
                    textTransform: "none",
                    color:
                      userDetails?.status === "ACTIVE" ? "#B50B00" : "#0476E2",
                    borderColor:
                      userDetails?.status === "ACTIVE" ? "#B50B00" : "#0476E2",
                  }}
                  variant="outlined"
                  onClick={handleActivateDeactivateUser}
                  disabled={readOnly_GlobalAdmin}
                >
                  {userDetails?.status === "ACTIVE"
                    ? "Deactivate"
                    : "Reactivate"}
                </Button>
                <Button
                  sx={{
                    textTransform: "none",
                    color: "#B50B00",
                    borderColor: "#B50B00",
                  }}
                  variant="outlined"
                  onClick={() => handleDeleteModalOpen(userDetails?.id)}
                  disabled={readOnly_GlobalAdmin}
                >
                  Delete
                </Button>
              </DialogActions>
            )}

            {isEditMode && (
              <DialogActions disabled={readOnly_GlobalAdmin}>
                <Button
                  sx={{
                    textTransform: "none",
                    color: "#2A2C2D",
                    bgcolor: "#1214151A",
                  }}
                  variant="contained"
                  onClick={handleCancelUserEdit}
                  disabled={readOnly_GlobalAdmin}
                >
                  Cancel
                </Button>

                <Button
                  sx={{
                    textTransform: "none",
                  }}
                  variant="contained"
                  onClick={handleSaveUserEdit}
                  disabled={readOnly_GlobalAdmin}
                >
                  Save
                </Button>
              </DialogActions>
            )}

            {userDetails?.status === "PENDING" ? (
              <DialogActions disabled={readOnly_GlobalAdmin}>
                <Button
                  sx={{
                    textTransform: "none",
                  }}
                  variant="contained"
                  onClick={handleAcceptModalOpen}
                  disabled={readOnly_GlobalAdmin}
                >
                  Accept
                </Button>

                <Button
                  sx={{
                    textTransform: "none",
                    color: "#B50B00",
                    borderColor: "#B50B00",
                  }}
                  variant="outlined"
                  onClick={() => handleRejectModalOpen(userDetails?.id)}
                  disabled={readOnly_GlobalAdmin}
                >
                  Reject
                </Button>
              </DialogActions>
            ) : null}

            <IconButton
              aria-label="close"
              onClick={handleUserDetailsClose}
              sx={(theme) => ({
                color: theme.palette.grey[500],
                width: "32px",
                height: "32px",
              })}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        <Box
          sx={{
            maxWidth: "727px",
            flex: 1,
            display: "flex",
            gap: "16px",
            flexDirection: "column",
            overflowY: "auto",
            minHeight: 0,
          }}
        >
          <CustomBoxRow>
            <CustomBoxRowItem>
              <CustomLabelTypo>Name</CustomLabelTypo>
              <CustomTextField
                key={`name-${userDetails?.id}`}
                disabled={disabled}
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                variant="outlined"
              />
            </CustomBoxRowItem>

            <CustomBoxRowItem>
              <CustomLabelTypo>Email</CustomLabelTypo>
              <CustomTextField
                key={`email-${userDetails?.id}`}
                disabled={disabled}
                value={userEmail}
                onChange={(e) => setUserEmail(e.target.value)}
                variant="outlined"
              />
            </CustomBoxRowItem>
          </CustomBoxRow>

          <CustomBoxRow>
            <CustomBoxRowItem>
              <CustomLabelTypo>Company Name</CustomLabelTypo>
              <CustomTextField
                key={`company-${userDetails?.id}`}
                disabled={disabled}
                value={companyNameState}
                onChange={(e) => setCompanyNameState(e.target.value)}
                variant="outlined"
              />
            </CustomBoxRowItem>

            <CustomBoxRowItem>
              <CustomLabelTypo>Company Website</CustomLabelTypo>
              <CustomTextField
                key={`website-${userDetails?.id}`}
                disabled={disabled}
                value={companyWebsiteURL}
                onChange={(e) => setCompanyWebsiteURL(e.target.value)}
                variant="outlined"
              />
            </CustomBoxRowItem>
          </CustomBoxRow>

          <CustomBoxRow>
            <CustomBoxRowItem>
              <CustomLabelTypo>Role</CustomLabelTypo>
              <CustomTextField disabled={true} value={userDetails?.role} />
            </CustomBoxRowItem>

            <CustomBoxRowItem>
              <CustomLabelTypo>Account Activation Date</CustomLabelTypo>
              <CustomTextField
                disabled={true}
                value={new Date(userDetails?.approvedDate).toLocaleDateString(
                  "en-US"
                )}
              />
            </CustomBoxRowItem>
          </CustomBoxRow>

          <CustomBoxRow>
            <CustomBoxRowItem>
              <CustomLabelTypo>Status</CustomLabelTypo>
              <CustomTextField disabled={true} value={userDetails?.status} />
            </CustomBoxRowItem>

            <CustomBoxRowItem>
              <CustomLabelTypo>Agreement Accepted</CustomLabelTypo>
              <CustomTextField 
                disabled={true} 
                value={agreementAcceptedValue}
              />
            </CustomBoxRowItem>
          </CustomBoxRow>

          <CustomBoxRow>
            <CustomBoxRowItem>
              <CustomLabelTypo>OEM Domain</CustomLabelTypo>
              <CustomTextField disabled={true} value={userDetails?.domain} />
            </CustomBoxRowItem>
          </CustomBoxRow>

          <Box
            sx={{
              maxWidth: "727px",
              minHeight: "63px",
              display: "flex",
              flexDirection: "column",
              gap: "4px",
            }}
          >
            <CustomLabelTypo>MFA Authentication</CustomLabelTypo>
            <CustomSingleInput
              disabled={true}
              value="Enabled"
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button
                        onClick={handleViewButtonClick}
                        variant="outlined"
                        size="small"
                        disabled={disabled}
                        sx={{
                          textTransform: "none",
                          width: "auto",
                          height: "auto",
                        }}
                      >
                        Disable
                      </Button>
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Box>

          <Box
            sx={{
              maxWidth: "727px",
              minHeight: "63px",
              display: "flex",
              flexDirection: "column",
              gap: "4px",
            }}
          >
            <CustomLabelTypo>Accepted Agreement</CustomLabelTypo>
            <Box
              sx={{
                maxWidth: "727px",
                minHeight: "40px",
                borderRadius: "4px",
                backgroundColor: "#F3F3F3",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "4px 8px",
                gap: "16px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "16px",
                  flex: 1,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: "#717273",
                    fontWeight: 400,
                    whiteSpace: "nowrap",
                  }}
                >
                  Agreement version
                </Typography>
                <Box
                  sx={{
                    padding: "4px 12px",
                    backgroundColor: "#E8E8E8",
                    border: "1px solid #E0E0E0",
                    borderRadius: "4px",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "14px",
                      color: "#2A2C2D",
                      fontWeight: 600,
                    }}
                  >
                    {agreementTypeLabel}
                  </Typography>
                </Box>
              </Box>
              <Button
                onClick={handleViewAgreementClick}
                variant="outlined"
                size="small"
                disabled={
                  displayUserDetails?.status === "PENDING" || 
                  !displayUserDetails?.agreementDetails?.url
                }
                sx={{
                  textTransform: "none",
                  width: "auto",
                  height: "auto",
                  flexShrink: 0,
                }}
              >
                View Agreement
              </Button>
            </Box>
          </Box>
        </Box>
      </BootstrapDialog>

      <AcceptNewUserModal
        userDetails={userDetails}
        openAcceptNewUserModal={openAcceptNewUserModal}
        handleAcceptUserClose={handleAcceptModalClose}
        searchTerm={searchTerm}
      />

      <RejectUserModal
        openRejectUserModal={openRejectUserModal}
        handleRejectUserClose={handleRejectModalClose}
        searchTerm={searchTerm}
        selectedRowId={selectedRowId}
      />

      <DeactivateModal
        selectedRowIdId={selectedRowId}
        handleVertIconModalClose={() => {}}
        openDeactivateModal={openDeactivateModal}
        handleDeactivateClose={handleDeactivateClose}
        searchTerm={searchTerm}
        anchorEl={null}
        setAnchorEl={() => {}}
      />

      <DeleteUserModal
        openDeleteUserModal={openDeleteUserModal}
        handleDeleteUserClose={handleDeleteUserClose}
        anchorEl={null}
        setAnchorEl={() => {}}
        currentTabStatus={currentTabStatus}
        selectedRowId={selectedRowId}
        searchTerm={searchTerm}
      />
    </>
  );
}

UserDetailsModal.propTypes = {
  openUserDetailsModal: PropTypes.bool.isRequired,
  handleUserDetailsClose: PropTypes.func.isRequired,
  userDetails: PropTypes.shape({
    id: PropTypes.string,
    firstName: PropTypes.string,
    givenName: PropTypes.string,
    familyName: PropTypes.string,
    email: PropTypes.string,
    companyName: PropTypes.string,
    companyWebsite: PropTypes.string,
    role: PropTypes.string,
    status: PropTypes.string,
    approvedDate: PropTypes.string,
    domain: PropTypes.string,
  }),
  searchTerm: PropTypes.string,
  currentTabStatus: PropTypes.string,
};

export default UserDetailsModal;
